import {
    faBuilding,
    faCalendarCheck,
    faCalendarPlus,
    faCheckCircle,
    faEnvelopeOpenText,
    faFileInvoiceDollar,
    faGraduationCap,
    faTimes,
    faUserPlus,
    faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Backdrop, Box, Fade, Grid, Modal, Paper, Typography } from '@mui/material'
import { useLang } from '../../../language';

// Guía paso a paso del módulo de empresas: cómo pasa una empresa de "no existe" a
// tener su espacio autorizado con personas inscritas en actividades.
//
// El esqueleto es el mismo que `ModalBuscarEmpresa` (Backdrop + Fade + caja centrada por
// `style`), pero con columnas de `Box` y no de `Grid`.
//
// El contenedor NO es un `Grid container` a propósito: MUI le pone `flex-wrap: wrap` y
// `flex-basis: 100%` a los hijos, y con `flexDirection: column` ese 100% es de la ALTURA.
// Cuando el contenido pasaba de `92vh` (consola abierta, móvil, ventana baja) los hijos
// no cabían y, por el `wrap`, saltaban a una segunda columna: las cajas se descuadraban
// unas al lado de otras. Con `Box` + `flex` normal el cuerpo simplemente hace scroll.
//
// 1100 de ancho: a dos columnas (`md={6}`) cada tarjeta queda a ~540px, suficiente para
// leer el párrafo en dos o tres líneas. En móvil cae a una columna.
const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1100,
    maxWidth: '95%',
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column' as 'column',
    gap: 1,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: { xs: 2, md: 3 },
};

export const ModalInstrucciones = ({ open, onClose }: any) => {

    const Lang = useLang()

    // Se arma dentro del componente y no como constante del módulo a propósito:
    // `Lang` cambia con el idioma activo, así que fuera del componente los textos se
    // quedarían congelados en el idioma con el que se cargó el módulo.
    const pasos = [
        {
            icono: faBuilding,
            titulo: Lang('GUIA_PASO_1_TITULO'),
            descripcion: Lang('GUIA_PASO_1_DESCRIPCION'),
            donde: Lang('GUIA_PASO_1_DONDE'),
        },
        {
            icono: faEnvelopeOpenText,
            titulo: Lang('GUIA_PASO_2_TITULO'),
            descripcion: Lang('GUIA_PASO_2_DESCRIPCION'),
            donde: Lang('GUIA_PASO_2_DONDE'),
        },
        {
            icono: faCheckCircle,
            titulo: Lang('GUIA_PASO_3_TITULO'),
            descripcion: Lang('GUIA_PASO_3_DESCRIPCION'),
            donde: Lang('GUIA_PASO_3_DONDE'),
        },
        {
            icono: faUserPlus,
            titulo: Lang('GUIA_PASO_4_TITULO'),
            descripcion: Lang('GUIA_PASO_4_DESCRIPCION'),
            donde: Lang('GUIA_PASO_4_DONDE'),
        },
        {
            icono: faCalendarPlus,
            titulo: Lang('GUIA_PASO_5_TITULO'),
            descripcion: Lang('GUIA_PASO_5_DESCRIPCION'),
            donde: Lang('GUIA_PASO_5_DONDE'),
        },
        {
            icono: faCalendarCheck,
            titulo: Lang('GUIA_PASO_6_TITULO'),
            descripcion: Lang('GUIA_PASO_6_DESCRIPCION'),
            donde: Lang('GUIA_PASO_6_DONDE'),
        },
        {
            icono: faGraduationCap,
            titulo: Lang('GUIA_PASO_7_TITULO'),
            descripcion: Lang('GUIA_PASO_7_DESCRIPCION'),
            donde: Lang('GUIA_PASO_7_DONDE'),
        },
        {
            icono: faFileInvoiceDollar,
            titulo: Lang('GUIA_PASO_8_TITULO'),
            descripcion: Lang('GUIA_PASO_8_DESCRIPCION'),
            donde: Lang('GUIA_PASO_8_DONDE'),
        },
        {
            icono: faUserShield,
            titulo: Lang('GUIA_PASO_9_TITULO'),
            descripcion: Lang('GUIA_PASO_9_DESCRIPCION'),
            donde: Lang('GUIA_PASO_9_DONDE'),
        },
    ]

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-instrucciones"
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 200,
            }}
        >
            <Fade in={open}>

                <Box sx={style} className='z-depth-4'>

                    {/* La guía es puramente informativa, así que no lleva pie: se cierra
                        con la X de arriba, con el `onClose` del `Modal` (clic fuera o
                        Escape) y con el botón de la página. */}
                    <Box
                        component='section'
                        className='d-flex align-items-center justify-content-between border-0'
                        sx={{ flexShrink: 0, gap: 2 }}
                    >

                        <Typography variant="h6" component="h2" sx={{ my: 0 }}>
                            {Lang('GUIA_EMPRESAS')}
                        </Typography>

                        {/* El `onClick` va en el `Box` y no en el icono para ampliar el
                            área pulsable (`p` + `mr` negativo para no descuadrar el
                            margen respecto al título). */}
                        <Box
                            onClick={onClose}
                            aria-label={Lang('CERRAR')}
                            title={Lang('CERRAR')}
                            className='pointer d-flex align-items-center'
                            sx={{ p: 0.5, mr: -0.5, flexShrink: 0 }}
                        >
                            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '1.1rem' }} />
                        </Box>

                    </Box>

                    {/* Sin `maxHeight` en píxeles ni en `vh`: el alto lo pone el flex del
                        contenedor (`flex: 1 1 auto` + `minHeight: 0`). Un tope fijo aquí
                        era lo que hacía que el modal se saliera de la pantalla cuando el
                        viewport se encogía. */}
                    <Box
                        component='section'
                        sx={{
                            flex: '1 1 auto',
                            minHeight: 0,
                            minWidth: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            pr: 0.5,
                        }}
                    >

                        <Typography variant='body2' sx={{ mt: 0, mb: { xs: 2, md: 3 }, color: 'text.secondary' }}>
                            {Lang('GUIA_EMPRESAS_DESCRIPCION')}
                        </Typography>

                        <Grid container spacing={{ xs: 1.5, md: 2 }}>
                            {
                                pasos.map((paso: any, index: number) => (

                                    <Grid item xs={12} md={6} key={index}>
                                        <Paper variant="outlined" sx={{ height: '100%', p: { xs: 2, md: 3 } }}>

                                            <Box sx={{ display: 'flex', gap: { xs: 1, md: 1.5 }, alignItems: 'flex-start' }}>

                                                {/* Círculo con el icono del paso. El número va en el
                                                    título: el icono identifica el paso de un vistazo
                                                    y el número marca el orden. */}
                                                <Box
                                                    sx={{
                                                        flex: '0 0 auto',
                                                        width: 34,
                                                        height: 34,
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'primary.main',
                                                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={paso.icono} />
                                                </Box>

                                                {/* `minWidth: 0` para que el texto pueda encoger
                                                    y partir línea en vez de estirar la tarjeta
                                                    (los hijos flex no bajan de su contenido si
                                                    no se les deja). */}
                                                <Box sx={{ minWidth: 0 }}>

                                                    <Typography variant='subtitle2' className='fw-500' sx={{ my: 0 }}>
                                                        {index + 1}. {paso.titulo}
                                                    </Typography>

                                                    <Typography variant='body2' sx={{ my: 1 }}>
                                                        {paso.descripcion}
                                                    </Typography>

                                                    <Typography variant='caption' sx={{ my: 0, color: 'text.secondary' }}>
                                                        {Lang('GUIA_PASO_DONDE')}: {paso.donde}
                                                    </Typography>

                                                </Box>

                                            </Box>

                                        </Paper>
                                    </Grid>

                                ))
                            }
                        </Grid>

                    </Box>

                </Box>

            </Fade>
        </Modal>
    )
}